import { useEffect, useState } from "react";
import {
  useParams,
  useSearchParams,
  useNavigate,
} from "react-router-dom";
import { db } from "../../firebase";
import {
  doc, getDoc, updateDoc, addDoc, collection,
  query, where, getDocs, serverTimestamp,
} from "firebase/firestore";

const EditClient = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const company_id = user.company_id || "";

  // ✅ siteId (real Firestore doc id) instead of siteIndex (array position)
  const siteId    = searchParams.get("siteId");
  const addSite   = searchParams.get("addSite");
  const isAddingSite  = addSite === "true";
  const isEditingSite = !!siteId;

  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    client_name: "",
    contact: "",
    gstin: "",
  });

  const [existingSites, setExistingSites] = useState([]); // from sites collection — SOT

  const [siteData, setSiteData] = useState({
    site_name: "",
    address: "",
    location_link: "",
    lat: null,
    lng: null,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const clientRef  = doc(db, "clients", id);
        const clientSnap = await getDoc(clientRef);

        if (!clientSnap.exists()) {
          alert("Client not found ❌");
          navigate("/master/client-list");
          return;
        }

        const client = clientSnap.data();
        if (client.company_id !== company_id) {
          alert("Access denied — this client belongs to a different company");
          navigate("/master/client-list");
          return;
        }
        setFormData({
          client_name: client.client_name || "",
          contact:     client.contact     || "",
          gstin:       client.gstin       || "",
        });

        // Sites — always fetched from SOT collection, never from client.sites
        const siteSnap = await getDocs(query(
          collection(db, "sites"),
          where("company_id", "==", company_id),
          where("client_id",  "==", id)
        ));
        const sites = siteSnap.docs.map(d => ({ _id: d.id, ...d.data() }));
        setExistingSites(sites);

        // Prefill if editing a specific existing site
        if (isEditingSite) {
          const target = sites.find(s => s._id === siteId);
          if (target) {
            setSiteData({
              site_name:     target.site_name     || "",
              address:       target.address       || "",
              location_link: target.location_link || "",
              lat:           target.lat || null,
              lng:           target.lng || null,
            });
          } else {
            alert("Site not found ❌");
            navigate(`/master/edit-client/${id}`);
          }
        }

      } catch (error) {
        console.error(error);
        alert("Failed to load client ❌");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, siteId, isEditingSite, navigate, company_id]);

  const handleClientChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSiteChange = (e) => {
    setSiteData({ ...siteData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Client-only edit (no site add/edit in progress)
      if (!isAddingSite && !isEditingSite) {
        await updateDoc(doc(db, "clients", id), {
          client_name: formData.client_name,
          contact:     formData.contact,
          gstin:       formData.gstin.trim().toUpperCase(),
        });
        setSuccess(true);
        setTimeout(() => navigate("/master/client-list"), 1000);
        return;
      }

      // Add new site — writes to sites collection (SOT), single governed pattern
      if (isAddingSite) {
        await addDoc(collection(db, "sites"), {
          company_id:      company_id,
          client_id:       id,
          client_name:     formData.client_name || "",
          site_name:       siteData.site_name.trim(),
          address:         siteData.address.trim(),
          location_link:   siteData.location_link || "",
          lat:             siteData.lat || null,
          lng:             siteData.lng || null,
          created_at:      serverTimestamp(),
          created_by:      user.uid || user.id || "",
          created_by_name: user.name || "",
        });
      }

      // Edit existing site — updates the real sites/{siteId} doc
      if (isEditingSite) {
        await updateDoc(doc(db, "sites", siteId), {
          site_name:     siteData.site_name.trim(),
          address:       siteData.address.trim(),
          location_link: siteData.location_link || "",
          lat:           siteData.lat || null,
          lng:           siteData.lng || null,
          updated_at:    serverTimestamp(),
        });
      }

      setSuccess(true);
      setTimeout(() => navigate("/master/client-list"), 1000);

    } catch (error) {
      console.error(error);
      alert("Update failed ❌");
    }
  };

  if (loading) {
    return <div className="text-center py-20 text-lg">Loading client...</div>;
  }

  return (
    <div className="max-w-5xl mx-auto bg-white p-8 rounded-2xl shadow border mt-8">

      {/* HEADER */}
      <div className="flex justify-between items-center mb-6">
        <button
          onClick={() => navigate("/master/client-list")}
          className="bg-gray-200 px-4 py-2 rounded-lg hover:bg-gray-300"
        >
          ← Back
        </button>

        <h2 className="text-3xl font-bold">
          {isAddingSite ? "Add New Site" : isEditingSite ? "Edit Site" : "Edit Client"}
        </h2>

        <div></div>
      </div>

      {success && (
        <div className="bg-green-100 text-green-700 p-3 rounded-xl mb-4">
          Updated successfully ✔
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">

        {/* CLIENT DETAILS — hidden while adding/editing a site to avoid accidental client rename mid-site-edit */}
        {!isAddingSite && !isEditingSite && (
          <div className="grid grid-cols-2 gap-4">
            <input
              type="text" name="client_name"
              value={formData.client_name}
              onChange={handleClientChange}
              placeholder="Client Name"
              className="border p-3 rounded-xl"
              required
            />
            <input
              type="text" name="contact"
              value={formData.contact}
              onChange={handleClientChange}
              placeholder="Contact Number"
              className="border p-3 rounded-xl"
              required
            />
            <input
              type="text" name="gstin"
              value={formData.gstin}
              onChange={handleClientChange}
              placeholder="Client GSTIN (if GST-registered)"
              maxLength={15}
              className="border p-3 rounded-xl col-span-2 uppercase"
            />
          </div>
        )}

        {/* SITE SECTION */}
        {(isAddingSite || isEditingSite) && (
          <div className="border-t pt-6 space-y-4">
            <h3 className="text-xl font-semibold">Site Details</h3>

            <input
              type="text" name="site_name"
              value={siteData.site_name}
              onChange={handleSiteChange}
              placeholder="Site Name"
              className="border p-3 rounded-xl w-full"
              required
            />

            <input
              type="text" name="address"
              value={siteData.address}
              onChange={handleSiteChange}
              placeholder="Address"
              className="border p-3 rounded-xl w-full"
              required
            />

            <input
              type="text" name="location_link"
              value={siteData.location_link}
              onChange={handleSiteChange}
              placeholder="Google Map Link"
              className="border p-3 rounded-xl w-full"
            />

            <div className="flex gap-2 items-center mt-2">
              <input readOnly
                value={siteData.lat && siteData.lng ? `${siteData.lat}, ${siteData.lng}` : ""}
                placeholder="Capture GPS at site location"
                className="flex-1 border p-3 rounded-xl text-sm bg-gray-50 text-gray-500"
              />
              <button type="button"
                onClick={() => {
                  if (!navigator.geolocation) { alert("GPS not supported"); return; }
                  navigator.geolocation.getCurrentPosition(
                    pos => {
                      setSiteData(prev => ({
                        ...prev,
                        lat: pos.coords.latitude,
                        lng: pos.coords.longitude,
                        location_link: prev.location_link ||
                          `https://www.google.com/maps?q=${pos.coords.latitude},${pos.coords.longitude}`,
                      }));
                    },
                    () => alert("Location permission denied")
                  );
                }}
                className="px-4 py-3 bg-teal-600 text-white rounded-xl text-sm font-semibold whitespace-nowrap">
                📍 Capture GPS
              </button>
            </div>
            {siteData.lat && siteData.lng && (
              <p className="text-xs text-teal-600 mt-1 font-semibold">
                ✅ GPS saved — {siteData.lat.toFixed(5)}, {siteData.lng.toFixed(5)}
              </p>
            )}
          </div>
        )}

        {/* EXISTING SITES — from sites collection (SOT), real site._id used */}
        {!isAddingSite && !isEditingSite && existingSites.length > 0 && (
          <div className="border-t pt-6 space-y-4">
            <h3 className="text-xl font-semibold">Existing Sites</h3>

            {existingSites.map((site) => (
              <div key={site._id} className="border rounded-xl p-4 bg-gray-50">
                <h4 className="font-bold text-lg">{site.site_name || "Unnamed Site"}</h4>
                <p>{site.address || "No address"}</p>

                {site.location_link && (
                  <a href={site.location_link} target="_blank" rel="noreferrer"
                    className="text-blue-600 underline text-sm">
                    Open Map
                  </a>
                )}

                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => navigate(`/master/edit-client/${id}?siteId=${site._id}`)}
                    className="bg-yellow-100 text-yellow-700 px-4 py-2 rounded-lg text-sm"
                  >
                    Edit This Site
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* SAVE */}
        <button
          type="submit"
          className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-semibold text-lg"
        >
          {isAddingSite ? "Add Site" : isEditingSite ? "Update Site" : "Update Client"}
        </button>

      </form>
    </div>
  );
};

export default EditClient;